import { CircularProgress, Tooltip } from "@material-ui/core";
import { Delete, Restore } from "@material-ui/icons";
import ConfirmationButton from "components/ConfirmationButton";
import { RootState } from "config/store";
import { COLORS } from "config/theme";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { hasAuthority } from "shared/authorization";
import { deleteCompany } from "shared/network/company.api";
import { deleteIssue } from "shared/network/issues.api";
import { deleteItem } from "shared/network/item.api";
import { deleteProject } from "shared/network/project.api";
import { Company, Issue, Item, Project } from "shared/types";

const DeleteButton = ({
  row,
  refetch,
  type,
}: {
  row: any;
  refetch: () => void;
  type: "ITEM" | "COMPANY" | "PROJECT" | "ISSUE";
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [loadingRowId, setLoadingRowId] = useState<number | null>(null);
  const { account, selectedRelTenant } = useSelector((state: RootState) => state.authentication);
  const tenant = selectedRelTenant.tenant;

  function getText(type: string, isDeleted: boolean) {
    switch (type) {
      case "ITEM":
        return {
          title: isDeleted ? "Termék törlésének visszavonása" : "Termék törlése",
          body: isDeleted
            ? "Biztosan visszaállítja ezt a terméket?"
            : "Biztosan törli ezt a terméket?",
        };
      case "COMPANY":
        return {
          title: isDeleted ? "Partner törlésének visszavonása" : "Partner törlése",
          body: isDeleted
            ? "Biztosan visszaállítja ezt a partnert?"
            : "Biztosan törli ezt a partnert?",
        };
      case "PROJECT":
        return {
          title: isDeleted ? "Projekt törlésének visszavonása" : "Projekt törlése",
          body: isDeleted
            ? "Biztosan visszaállítja ezt a projektet?"
            : "Biztosan törli ezt a projektet?",
        };
      case "ISSUE":
        return {
          title: isDeleted ? "Feladat törlésének visszavonása" : "Feladat törlése",
          body: isDeleted
            ? "Biztosan visszaállítja ezt a feladatot?"
            : "Biztosan törli ezt a feladatot?",
        };
      default:
        return {
          title: "",
          body: "",
        };
    }
  }

  return (
    <>
      {hasAuthority(account.user, account.permissions, selectedRelTenant, [
        type === "ITEM"
          ? "ITEM_ADMIN"
          : type === "COMPANY"
          ? "COMPANY_ADMIN"
          : type === "PROJECT"
          ? "PROJECT_ADMIN"
          : "ISSUE_ADMIN",
      ]) &&
        !!row && (
          <Tooltip title={getText(type, row.isDeleted)?.title}>
            <div>
              <ConfirmationButton
                buttonType="ICON"
                size="small"
                color="primary"
                disabled={loadingRowId === row.id}
                style={{ color: COLORS.red }}
                onSubmit={async () => {
                  setLoadingRowId(row.id);
                  try {
                    if (type === "ITEM") {
                      await deleteItem(tenant?.id, { ...row, isDeleted: !row.isDeleted } as Item);
                    }
                    if (type === "COMPANY") {
                      await deleteCompany(tenant?.id, {
                        ...row,
                        isDeleted: !row.isDeleted,
                      } as Company);
                    }
                    if (type === "PROJECT") {
                      await deleteProject(tenant?.id, {
                        ...row,
                        isDeleted: !row.isDeleted,
                      } as Project);
                    }
                    if (type === "ISSUE") {
                      await deleteIssue(tenant?.id, {
                        ...row,
                        isDeleted: !row.isDeleted,
                      } as Issue);
                    }
                    enqueueSnackbar(t("user.deleteSuccess"), { variant: "success" });
                    refetch();
                  } catch (error) {
                    enqueueSnackbar(t("user.deleteError"), { variant: "error" });
                  } finally {
                    setLoadingRowId(null);
                  }
                }}
                title={getText(type, row.isDeleted)?.title}
                body={getText(type, row.isDeleted)?.body}
              >
                {loadingRowId === row.id ? (
                  <CircularProgress size={24} />
                ) : row.isDeleted ? (
                  <Restore />
                ) : (
                  <Delete />
                )}
              </ConfirmationButton>
            </div>
          </Tooltip>
        )}
    </>
  );
};
export default DeleteButton;
